import React, { useCallback, useEffect, useState } from 'react'

import createRandomID from '../../../utils/createRandomID'

import MacroContainer from '../../_layout/Macro/MacroContainer'
import M047ViewSwitch from './M047ViewSwitch/M047ViewSwitch'
import { M047PropTypes } from './M047PropTypes'

const M047 = ({ elements, settings }) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [modifiedElements, setModifiedElements] = useState()

  const points = calcSum()

  const initElements = useCallback(() => {
    // Add a ID based on elementIndex and optionIndex to each option, so that it's possible to navigate between questions and store the user input.
    const preparedElements = elements.map((element, elementIndex) => {
      if (element.options) {
        const editedOptions = element.options.map((option, optionIndex) => {
          return { ...option, id: `${elementIndex}-${optionIndex}`, selected: getDefaultSelection(element?.type) }
        })
        return { ...element, options: editedOptions, id: createRandomID('element') }
      } else {
        return element
      }
    })

    setModifiedElements(preparedElements)
  }, [elements])

  // Returns the default selection for each question type
  const getDefaultSelection = (type) => {
    switch (type) {
      case 'single-selection':
      case 'multi-selection':
      case 'multi-selection-chip':
        return false

      case 'range-slider-selection':
        return 0

      default:
        return null
    }
  }

  // Sums up all the points of all questions the user answered
  function calcSum() {
    return (
      modifiedElements?.reduce((acc, element) => {
        if (!element?.options?.length) return acc

        let elementSum = 0
        switch (element.type) {
          case 'single-selection':
          case 'multi-selection':
          case 'multi-selection-chip':
            elementSum = element.options.reduce((acc, option) => {
              if (option?.selected) {
                return acc + option.weight
              }
              return acc
            }, 0)
            break

          // "selected" is the value of the range slider which represents the points
          case 'range-slider-selection':
            elementSum = element.options.reduce((acc, option) => {
              return acc + option?.selected
            }, 0)
            break

          default:
            break
        }

        return acc + elementSum
      }, 0) ?? 0
    )
  }

  // init
  useEffect(() => {
    initElements()
  }, [initElements])

  // When next button is clicked
  const navigateToNextPage = () => {
    setCurrentPage(currentPage + 1)
  }

  // When prev button is clicked
  const navigateToPreviousPage = () => {
    setCurrentPage(currentPage - 1)
  }

  // Gets called when user clicks restart button
  const startAgain = () => {
    // Reset all selected options in the data model
    initElements()
    // navigate to first page
    setCurrentPage(0)
  }

  const updateCurrentElementOptions = (updatedOptions) => {
    // Update the options of the current element, so that the user selection is stored in the object
    const updatedElements = [
      ...modifiedElements.slice(0, currentPage),
      { ...modifiedElements[currentPage], options: updatedOptions },
      ...modifiedElements.slice(currentPage + 1),
    ]
    setModifiedElements(updatedElements)
  }

  // Calcs the progress bar with
  // Progess bar should be updated so that it's not counting from the first page on.
  const calcWidth = () => {
    // Introduction page
    if (currentPage === 0) {
      return '0'
    }
    // Summary page
    else if (currentPage === elements.length - 1) {
      return '100'
    } else {
      const quizLength = elements.length - 2
      return Math.round((currentPage / quizLength) * 90).toString()
    }
  }

  if (!modifiedElements?.length) return null

  return (
    <MacroContainer dataTrackingName="M047">
      <div className="relative bg-e8f4f2 py-40 600:py-48">
        <div className={`absolute  top-0 left-0 right-0 col-span-full`}>
          <div
            style={{ width: `${calcWidth()}%` }}
            className={`h-4 bg-005e3f transition-width duration-500 ease-in-out`}
          />
        </div>
        {currentPage > 0 && currentPage < elements.length - 1 && (
          <div className="pl-16 720:pl-48 1000:pl-64 text-586167">
            {currentPage}/{elements.length - 2}
          </div>
        )}
        <M047ViewSwitch
          key={modifiedElements[currentPage]?.id} // use key to re-render the component when the view changes; this way the component gets resetted properly
          points={points}
          element={modifiedElements[currentPage]}
          startAgain={startAgain}
          navigateToNextPage={navigateToNextPage}
          navigateToPreviousPage={navigateToPreviousPage}
          updateCurrentElementOptions={updateCurrentElementOptions}
          quizType={settings?.quizType}
        />
      </div>
    </MacroContainer>
  )
}

M047.propTypes = M047PropTypes

M047.defaultProps = {
  elements: [],
}

export default M047
